import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { Dimensions } from "@amzn/asx-ui-telemetry-client";
import {
    CloudWatchRumProvider,
    TelemetryClient as ASXTelemetryClient,
    monitorActiveTimeSpent,
    monitorPageViews,
    monitorAndReportErrors,
    TaktProvider,
} from "@amzn/asx-ui-telemetry-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { SdkError } from "@aws-sdk/types";

import { getAuthSettings } from "../api/websiteSettings";
import type {
    TelemetryFilterModel,
    UnifiedFilterTelemetry,
} from "../constants/telemetry";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../constants/telemetry";
import { authStore } from "../store/auth";
import { FeatureFlags, FeatureFlagsStore } from "../store/featureFlags";
import { navigationStore } from "../store/navigation";
import { unifiedFilterStore } from "../store/unifiedFilters";
import type {
    BaseFilterOptionModel,
    DiscoveryFieldSelection,
    TypeFilterOptionModel,
} from "../types/filters/common";
import {
    CoreUnifiedFilterKeys,
    UnifiedFilterKeys,
} from "../types/filters/unified";
import type {
    YourInsightFilterContentSubType,
    YourInsightFilterContentType,
    YourInsightsFilters,
} from "../types/filters/yourInsights";
import type {
    IHubContentSubType,
    IHubContentType,
} from "../types/ihubContentType";

import { APPENV } from "./app.environment";
import { getAdvertiserId } from "./permission.utils";
import { getFilterStore } from "./tab.utils";

// We have Cradle jobs that export these TAKT events to quicksight, but require that each value start with 'ihub'
// To ensure this is always true, we will use this prefix to append onto each takt context if it doesn't exist
// Cradle Job - https://datacentral.a2z.com/cradle#/client-deck-builder-prod/profiles/e31047f1-e7db-4982-a803-ff1931eb86c9
export const IHUB_TAKT_PREFIX = "ihub";

let cachedEnabledWeblabs: string[] | null = null;

// Subscribe to feature flags store to invalidate cache when flags change
FeatureFlagsStore.subscribe(() => {
    cachedEnabledWeblabs = null;
});

const getEnabledWeblabs = (): string[] => {
    if (cachedEnabledWeblabs === null) {
        cachedEnabledWeblabs = Object.values(FeatureFlags).filter(
            (flag) => FeatureFlagsStore.getState()[flag],
        );
    }
    return cachedEnabledWeblabs;
};

/**
 * Log a custom event to Takt and CloudWatch RUM.
 *
 * @param {string} context - the functional area where the event was triggered.
 * @param {Object} details - the details of the event.
 */
export const logWithTakt = (context: string, details: any = {}): void => {
    // Ensure context starts with IHUB_TAKT_PREFIX
    const formattedContext = context.startsWith(IHUB_TAKT_PREFIX)
        ? context
        : `${IHUB_TAKT_PREFIX}-${context}`;

    const enrichedDetails = {
        ...details,
        requesterAlias: authStore.getState().username,
        activeTab: navigationStore.getState().activeTab,
        advertiserId: getAdvertiserId(),
    };

    // Log to TAKT (existing functionality)
    if (window && (window as any).AmazonTakt) {
        (window as any).AmazonTakt.logCustom({
            context: formattedContext,
            details: JSON.stringify(enrichedDetails),
        });
    }

    logTaktInDev({
        context: formattedContext,
        details: enrichedDetails,
    });
};

/**
 * Log a visit to Takt.
 *
 * @param {Object} metadata - the metadata of the event.
 */

export const logVisitMetadata = (metadata: Record<string, any>): void => {
    const taktLog = {
        ...metadata,
        requesterAlias: authStore.getState().username,
    };
    if (window && (window as any).AmazonTakt) {
        (window as any).AmazonTakt.addVisitMetadata(taktLog);
    }
    logTaktInDev(taktLog, "visit");
};

const logTaktInDev = (taktLog: any, scope: string = "custom") => {
    if (
        APPENV.dev_server &&
        taktLog.context !== `${IHUB_TAKT_PREFIX}-${TELEMETRY_ID.INSIGHT_CARD}`
    ) {
        // eslint-disable-next-line no-console
        console.log(`Log ${scope} - ${JSON.stringify(taktLog, null, 2)}`);
    }
};

const metricsErrorHandler = (err: SdkError) => {
    console.error(err);
};

export const getFilterTelemetryDetails = (): {
    filters: DiscoveryFieldSelection | YourInsightsFilters;
    contentType: IHubContentType | YourInsightFilterContentType;
    contentSubType:
        | IHubContentSubType
        | YourInsightFilterContentSubType
        | undefined;
    searchQuery: string | undefined;
} => {
    const navigationTab = navigationStore.getState().activeTab;
    const {
        contentType,
        contentSubType,
        fieldSelections: filters,
        searchQuery,
    } = getFilterStore(navigationTab);

    return {
        filters,
        contentType,
        contentSubType,
        searchQuery,
    };
};

export const getUnifiedFilterTelemetryDetails = (): {
    filters: Partial<UnifiedFilterTelemetry>;
    searchQuery: string | undefined;
    sortOption: string | undefined;
} => {
    const unifiedFilters = unifiedFilterStore.getState();
    const searchQuery = unifiedFilters.searchQuery;
    const sortOption = unifiedFilters.sortAttribute;
    const allFilters = unifiedFilters.filters;

    const result: UnifiedFilterTelemetry = {
        [UnifiedFilterKeys.PACKAGE_NAME]: [],
        [UnifiedFilterKeys.ENTITY_NAME]: [],
        [UnifiedFilterKeys.TOPIC]: [],
        [UnifiedFilterKeys.ADVERTISER_GOAL]: [],
        [UnifiedFilterKeys.AD_ACTIVATION_CHANNEL]: [],
        [UnifiedFilterKeys.SEASONAL_EVENTS]: [],
        [UnifiedFilterKeys.LANGUAGE]: [],
        [UnifiedFilterKeys.MARKETING_STRATEGY]: [],
        [UnifiedFilterKeys.SUB_VERTICAL]: [],
        [UnifiedFilterKeys.VERTICAL]: [],
        [UnifiedFilterKeys.FILE_TYPE]: [],
        [UnifiedFilterKeys.BRAND_NAME]: [],
        [UnifiedFilterKeys.MARKETPLACE]: [],
        [UnifiedFilterKeys.OBJECTIVES]: [],
        [UnifiedFilterKeys.PRIMARY_CONTENT_CREATOR_TEAM]: [],
        [UnifiedFilterKeys.BROWSE_NODE_NAME]: [],
        [UnifiedFilterKeys.AD_PRODUCTS_AND_FORMATS]: [],
        [CoreUnifiedFilterKeys.TYPE]: getSelectedTypeFilters(
            allFilters.UNIFIED_TYPE,
        ),
        [UnifiedFilterKeys.MEETING_TYPE]: [],
        [UnifiedFilterKeys.MEETING_SUB_TYPE]: [],
        [UnifiedFilterKeys.OPPORTUNITY_CATEGORY]: [],
        [UnifiedFilterKeys.OPPORTUNITY_STATUS]: [],
    };

    for (const [category, items] of Object.entries(allFilters)) {
        if (category === CoreUnifiedFilterKeys.TYPE.toString()) continue;

        const selectedItems = (items as BaseFilterOptionModel[])
            .filter((item) => item.isSelected && !item.isDisabled)
            .map((item) => item.label);

        if (selectedItems.length > 0) {
            result[category] = selectedItems;
        } else {
            result[category] = undefined;
        }
    }

    return {
        filters: result,
        searchQuery,
        sortOption,
    };
};

const getSelectedTypeFilters = (
    type: TypeFilterOptionModel[],
): TelemetryFilterModel[] => {
    return type
        .filter((item) => item.isSelected && !item.isDisabled)
        .map((item) => {
            return {
                id: item.id,
                label: item.label,
            };
        });
};

// can't call a hook in this function to get the feature flag directly
export const logInsightBrowseResults = (
    totalResults: number = 0,
    useUnifiedSearch: boolean = false,
): void => {
    const filters = useUnifiedSearch
        ? getUnifiedFilterTelemetryDetails()
        : getFilterTelemetryDetails();
    logWithTakt(TELEMETRY_ID.NUMBER_OF_RESULTS, {
        ...filters,
        totalResults,
    });
};

// can't call a hook in this function to get the feature flag directly
export const logViewInsightDetailsClick = (
    identifier: string,
    resultPosition: number,
    totalResults: number,
    insightType?: InsightType,
    useUnifiedSearch: boolean = false,
    contentSubType?: string,
): void => {
    const filters = useUnifiedSearch
        ? getUnifiedFilterTelemetryDetails()
        : getFilterTelemetryDetails();
    logWithTakt(TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK, {
        ...filters,
        identifier,
        insightType,
        resultPosition,
        totalResults,
    });
    PhoenixMetrics.emit(
        TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK,
        PHOENIX_FEATURE.DISCOVERY,
        {
            contentInfo: insightType
                ? {
                      contentProvider: insightType,
                      contentType: contentSubType ?? "",
                      contentId: identifier,
                  }
                : undefined,
            customData: {
                ...filters,
                resultPosition,
                totalResults,
            },
        },
    );
};

const getConfig = () => {
    const authSettings = getAuthSettings();
    return {
        siteName: "ASX",
        serviceName: "IHub",
        sharedContext: {
            origin: window.location.href,
            userAgent: navigator.userAgent,
        },
        stage: authSettings.stage,
        region: authSettings.region,
        metricsErrorHandler,
        onBeforeUnload: () => {
            asxTelemetryClient.stopTimer(
                TELEMETRY_ID.TIMERS.APP_LOAD_TO_PAGE_CLOSE,
            );
        },
        urlParser: (url) => url.pathname,
    };
};

const plugins = [
    monitorActiveTimeSpent,
    monitorPageViews,
    monitorAndReportErrors,
];
const metrics = [
    {
        provider: new CloudWatchRumProvider(getConfig()),
        plugins,
    },
    {
        provider: new TaktProvider(getConfig()),
        plugins,
    },
];

export const asxTelemetryClient = new ASXTelemetryClient({
    metrics,
    config: getConfig(),
});

export const logWithAsxTelemetryClient = (
    operation: string,
    metricName: string,
    count = 1,
    details?: Dimensions,
): void => {
    try {
        if (details) {
            asxTelemetryClient.publishCounter(
                operation,
                metricName,
                count,
                details,
            );
        } else {
            asxTelemetryClient.publishCounter(operation, metricName, count);
        }
    } catch (error) {
        console.error(
            `Failed to publish telemetry counter ${operation}: ${(error as Error).message}`,
        );
    }
};

// Start the provided plugins
asxTelemetryClient.start();
